import React from 'react';
import { connect } from 'react-redux';
import { Box, Snackbar, Alert, Grid, Typography } from '@mui/material';

import BoxInc from '../../../shared-ui/BoxInc/Index';
import ButtonInc from '../../../shared-ui/ButtonInc';
import AlertDialog from '../../../components/AlertDialog/index.es6';

import ViewEmailTemplates from '../../EmailTemplates/components/ViewEmailTemplates';
import AddEmailTemplate from '../../EmailTemplates/components/AddEmailTemplate';
import EditEmailTemplate from '../../EmailTemplates/components/EditEmailTemplate';

import {
  getOrgEmailTemplatesRequest,
  createOrgEmailTemplateRequest,
  updateOrgEmailTemplateRequest,
  deleteOrgEmailTemplateRequest,
  resetCreateOrgEmailTemplateSuccess,
  resetCreateOrgEmailTemplateError,
  resetUpdateOrgEmailTemplateSuccess,
  resetUpdateOrgEmailTemplateError,
  resetDeleteOrgEmailTemplateSuccess,
  resetDeleteOrgEmailTemplateError,
} from '../../OrganizationEmailTemplates/Redux/actions';

class EmailTemplatesTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      openAddDialog: false,
      openEditDialog: false,
      selectedTemplate: null,
      showDeleteConfirm: false,
      templateToDelete: null,
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarSeverity: 'success',
    };
  }

  componentWillMount() {
    this.props.dispatch(
      getOrgEmailTemplatesRequest(this.props.organizationGuid)
    );
  }

  componentDidUpdate(prevProps) {
    var d = this.props.orgEmailTemplates;
    var p = prevProps.orgEmailTemplates;

    if (d.created && !p.created) {
      this.setState({ openAddDialog: false });
      this.openSnack('Email template created successfully', 'success');
      this.props.dispatch(resetCreateOrgEmailTemplateSuccess());
      this.refreshList();
    }
    if (d.createError && !p.createError) {
      this.openSnack('Create failed: ' + String(d.createError), 'error');
      this.props.dispatch(resetCreateOrgEmailTemplateError());
    }
    if (d.updated && !p.updated) {
      this.setState({ openEditDialog: false, selectedTemplate: null });
      this.openSnack('Email template updated successfully', 'success');
      this.props.dispatch(resetUpdateOrgEmailTemplateSuccess());
      this.refreshList();
    }
    if (d.updateError && !p.updateError) {
      this.openSnack('Update failed: ' + String(d.updateError), 'error');
      this.props.dispatch(resetUpdateOrgEmailTemplateError());
    }
    if (d.deleted && !p.deleted) {
      this.setState({ showDeleteConfirm: false, templateToDelete: null });
      this.openSnack('Email template deleted', 'success');
      this.props.dispatch(resetDeleteOrgEmailTemplateSuccess());
      this.refreshList();
    }
    if (d.deleteError && !p.deleteError) {
      this.openSnack('Delete failed: ' + String(d.deleteError), 'error');
      this.props.dispatch(resetDeleteOrgEmailTemplateError());
    }
  }

  refreshList() {
    this.props.dispatch(
      getOrgEmailTemplatesRequest(this.props.organizationGuid)
    );
  }

  openSnack(message, severity) {
    this.setState({
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: severity,
    });
  }

  toggleAddDialog() {
    this.setState({ openAddDialog: !this.state.openAddDialog });
  }

  handleCreate(formPayload) {
    // All fields captured by <AddEmailTemplate> (subject, filename,
    // recipients, cc_recipients, attachments, entity_attachments,
    // entity_type, notify_restriction, etc.) are preserved inside
    // template_data. code/language/active are promoted to top-level.
    var template_data = Object.assign({}, formPayload);
    delete template_data.guid;

    this.props.dispatch(createOrgEmailTemplateRequest({
      organization_guid: this.props.organizationGuid,
      code: formPayload.code,
      language: formPayload.language,
      template_data: template_data,
      active: formPayload.active !== false,
    }));
  }

  openEdit(row) {
    // Row is already the flattened shape from render(): template_data
    // fields hoisted + guid/code/language/active/created_at/updated_at.
    // That matches the prop contract expected by <EditEmailTemplate>.
    this.setState({ openEditDialog: true, selectedTemplate: row });
  }

  closeEdit() {
    this.setState({ openEditDialog: false, selectedTemplate: null });
  }

  handleUpdate(formPayload) {
    var tmpl = this.state.selectedTemplate;
    if (!tmpl) return;

    var template_data = Object.assign({}, formPayload);
    delete template_data.guid;

    this.props.dispatch(updateOrgEmailTemplateRequest({
      guid: tmpl.guid,
      organization_guid: this.props.organizationGuid,
      code: formPayload.code,
      language: formPayload.language,
      template_data: template_data,
      active: formPayload.active !== false,
    }));
  }

  confirmDelete(guid) {
    this.setState({ showDeleteConfirm: true, templateToDelete: guid });
  }

  cancelDelete() {
    this.setState({ showDeleteConfirm: false, templateToDelete: null });
  }

  handleDelete() {
    if (!this.state.templateToDelete) return;
    // templateToDelete stays set so the row keeps its spinner
    this.setState({ showDeleteConfirm: false });
    this.props.dispatch(deleteOrgEmailTemplateRequest({
      guid: this.state.templateToDelete,
      organization_guid: this.props.organizationGuid,
    }));
  }

  render() {
    var d = this.props.orgEmailTemplates;
    var state = this.state;

    var list = d.list || [];

    // Flatten template_data fields onto each row so <ViewEmailTemplates>
    // can read subject / entity_type / notify_restriction directly.
    var rows = list.map(function(t) {
      var td = t.template_data || {};
      return Object.assign({}, td, {
        guid: t.guid,
        code: t.code,
        language: t.language,
        active: t.active,
        created_at: t.created_at,
        updated_at: t.updated_at,
      });
    });

    return (
      <Box>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, color: '#111827' }}>
            Email Templates
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.25 }}>
            Org-scoped email templates.
          </Typography>
     
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BoxInc
              expanded
              title="Email Templates"
              actionButtons={[
                <ButtonInc
                  key="add"
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.toggleAddDialog();
                  }}
                >
                  Upload Email Template
                </ButtonInc>,
              ]}
            >
              <ViewEmailTemplates
                loading={d.loading}
                deleteLoading={d.deleting}
                deletingGuid={state.templateToDelete}
                templates={rows}
                editEmailTemplate={this.openEdit.bind(this)}
                deleteEmailTemplate={this.confirmDelete.bind(this)}
              />

              <AddEmailTemplate
                open={state.openAddDialog}
                close={this.toggleAddDialog.bind(this)}
                saveEmailTemplate={this.handleCreate.bind(this)}
                loading={d.creating}
              />

              <EditEmailTemplate
                open={state.openEditDialog}
                template={state.selectedTemplate}
                onClose={this.closeEdit.bind(this)}
                onSave={this.handleUpdate.bind(this)}
                loading={d.updating}
              />
            </BoxInc>
          </Grid>
        </Grid>

        {state.showDeleteConfirm && (
          <AlertDialog
            show
            title="Delete Email Template"
            type="warning"
            showConfirm
            showCancel
            confirmBtnText="Yes, Delete"
            cancelBtnText="Cancel"
            reverseConfirmAndCancelButtons
            onConfirm={this.handleDelete.bind(this)}
            onCancel={this.cancelDelete.bind(this)}
          >
            <div style={{ fontSize: 14, lineHeight: 1.4 }}>
              <p style={{ margin: '10px 0' }}>
                Are you sure you want to delete this email template?
              </p>
              <p style={{ margin: '10px 0' }}>
                <strong>This action cannot be undone.</strong>
              </p>
            </div>
          </AlertDialog>
        )}

        {/* SweetAlert closes itself on confirm, so progress needs its own alert */}
        {d.deleting && (
          <AlertDialog
            show
            title="Deleting"
            type="custom"
            showConfirm={false}
            showCancel={false}
          >
            <div style={{ fontSize: 14, padding: '10px 0' }}>
              Deleting email template, please wait...
            </div>
          </AlertDialog>
        )}

        <Snackbar
          open={state.snackbarOpen}
          autoHideDuration={4000}
          onClose={() => this.setState({ snackbarOpen: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={state.snackbarSeverity}
            onClose={() => this.setState({ snackbarOpen: false })}
          >
            {state.snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default connect(function(state) {
  return { orgEmailTemplates: state.orgEmailTemplatesData };
})(EmailTemplatesTab);
